/* Le selecteur de montage — voir snippets/simply-montage.liquid
 *
 * Vanilla, sans dependance, conformement a la regle du projet. Un seul element
 * personnalise, pas de framework, pas de build.
 *
 * Principe : la longueur est rendue par Liquid et fonctionne sans JavaScript —
 * on peut toujours acheter le ski nu. Les fixations et les peaux dependent de
 * la matrice de compatibilite, chargee en differe ; elles n'apparaissent que
 * lorsque le fichier est la.
 *
 * Doc de contenus §8 : une case « Add a binding » ouvre la liste des fixations
 * compatibles avec la longueur ; chaque ligne dit « Squire 11 — Light binding ·
 * CHF 199 · Details », la fixation recommandee porte « Recommended ». Aucune
 * quantite de stock n'est jamais affichee (§1.2 : pas de pression de stock).
 */
class SimplyMontage extends HTMLElement {
  connectedCallback() {
    this.produit = this.dataset.produit;
    this.titre = this.dataset.titre || '';
    this.devise = this.dataset.devise || 'CHF';
    this.fixationsPeaux = (this.dataset.fixationsPeaux || '').split(',').filter(Boolean);
    this.donnees = null;
    this.avecFixation = false;
    this.fixation = null;   // handle de la fixation choisie, ou null pour « ski nu »
    this.avecPeaux = false;

    this.zones = {
      fixations: this.querySelector('[data-zone="fixations"]'),
      listeFixations: this.querySelector('[data-liste="fixations"]'),
      fixationAutre: this.querySelector('[data-zone="fixation-autre"]'),
      peaux: this.querySelector('[data-zone="peaux"]'),
      recap: this.querySelector('[data-zone="recap"]'),
      total: this.querySelector('[data-zone="total"]'),
      totalLibelle: this.querySelector('[data-zone="total-libelle"]'),
      message: this.querySelector('[data-zone="message"]'),
      notify: this.querySelector('[data-zone="notify"]'),
      totalBloc: this.querySelector('.simply-montage__total'),
    };
    this.caseFixation = this.querySelector('input[name="simply-ajout-fixation"]');
    this.bouton = this.querySelector('[data-action="ajouter"]');
    this.textes = this.lit('[data-textes-montage]') || {};
    this.noms = this.lit('[data-fixations-montage]') || {};
    // Le libelle de repos vient du DOM, pas du bloc de textes : c'est Liquid qui
    // l'a ecrit, et il reste juste meme si le bloc JSON manque.
    this.libelleBouton = this.bouton.textContent.trim();

    this.addEventListener('change', (e) => {
      const nom = e.target.name;
      if (nom === 'simply-longueur') this.surLongueur();
      if (nom === 'simply-ajout-fixation') {
        this.avecFixation = e.target.checked;
        this.fixation = null;
        this.surLongueur();
      }
      if (nom === 'simply-fixation') {
        this.fixation = e.target.value || null;
        this.surLongueur();
      }
      if (nom === 'simply-peaux') {
        this.avecPeaux = e.target.checked;
        this.rafraichit();
      }
    });
    this.bouton.addEventListener('click', () => this.ajoute());

    this.charge();
  }

  /* Un bloc JSON ecrit par Liquid. Voir la note « Les libelles que le JS ecrit
   * lui-meme » dans snippets/simply-montage.liquid : les attributs portent le nom
   * du composant, parce que le finder rendu a l'interieur a les siens.
   * Un bloc absent ou illisible n'eteint pas le selecteur : la vente doit
   * survivre a une chaine manquante. */
  lit(selecteur) {
    const bloc = this.querySelector(selecteur);
    if (!bloc) {
      console.warn('simply-montage : bloc absent', selecteur);
      return null;
    }
    try {
      return JSON.parse(bloc.textContent);
    } catch (err) {
      console.warn('simply-montage : bloc illisible', selecteur, err);
      return null;
    }
  }

  /* Remplit les trous `%mot%` d'un libelle traduit. */
  remplit(modele, valeurs) {
    return Object.entries(valeurs).reduce(
      (texte, [cle, valeur]) => texte.split(`%${cle}%`).join(valeur),
      modele || ''
    );
  }

  async charge() {
    try {
      const rep = await fetch(this.dataset.donnees);
      if (!rep.ok) throw new Error(rep.status);
      const tout = await rep.json();
      this.donnees = tout[this.produit] || null;
    } catch (err) {
      // Sans les donnees, on ne bloque pas la vente : la fiche reste achetable
      // en ski nu. On ne montre pas d'erreur au client pour ca.
      this.donnees = null;
      console.warn('simply-montage : compatibilites indisponibles', err);
    }
    this.prixDansPopups();
    this.surLongueur();
  }

  get longueur() {
    const coche = this.querySelector('input[name="simply-longueur"]:checked');
    return coche ? coche.value : null;
  }

  get entree() {
    if (!this.donnees) return null;
    return this.donnees.longueurs[this.longueur] || null;
  }

  /* « CHF 1,188 » — la forme du doc de contenus (§1.4 : « CHF 989 / EUR 1,189 »,
   * pas de decimales inutiles). */
  prix(centimes) {
    return `${this.devise} ${Math.round(centimes / 100).toLocaleString('en-US')}`;
  }

  /* Les titres des pop-ups n'ont pas de prix cote Liquid : les fixations sont
   * depubliees. On le lit dans le JSON, sur la premiere longueur qui la porte. */
  prixDansPopups() {
    if (!this.donnees) return;
    const vus = {};
    for (const entree of Object.values(this.donnees.longueurs)) {
      for (const [handle, f] of Object.entries(entree.fixations)) vus[handle] = f.prix;
    }
    for (const [handle, centimes] of Object.entries(vus)) {
      const cible = document.querySelector(`#simply-fixation-${CSS.escape(handle)} [data-simply-prix]`);
      if (cible) cible.textContent = ` · ${this.prix(centimes)}`;
    }
  }

  /* Les libelles viennent de nos propres produits, mais un titre porte ce que
   * l'admin y met. On echappe plutot que de faire confiance a la source. */
  echappe(texte) {
    return String(texte ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }

  /* La fixation recommandee pour la longueur, ou la premiere disponible. */
  recommandee(e) {
    const ordre = this.donnees.ordre_fixations.filter((h) => e.fixations[h]);
    const vendable = (h) => e.fixations[h].vendable && e.fixations[h].dispo > 0;
    return ordre.find((h) => e.fixations[h].recommandee && vendable(h))
      || ordre.find(vendable)
      || null;
  }

  /* Longueur epuisee : ni fixation, ni peaux, ni bouton d'achat — le formulaire
   * « notify me » a la place (§3.3). Apres un envoi, Shopify recharge la fiche
   * sur une autre longueur : la zone reste ouverte pour montrer la reponse. */
  surRupture() {
    const coche = this.querySelector('input[name="simply-longueur"]:checked');
    const epuisee = Boolean(coche && coche.hasAttribute('data-epuisee'));
    const reponse = this.zones.notify && this.zones.notify.dataset.notifyOuvert === 'true';
    if (this.zones.notify) {
      this.zones.notify.hidden = !(epuisee || reponse);
      const corps = this.zones.notify.querySelector('[data-notify-corps]');
      if (corps && coche) corps.value = `Notify me: ${this.titre} ${coche.value} (variant ${coche.dataset.variante})`;
    }
    this.bouton.hidden = epuisee;
    this.zones.recap.hidden = epuisee;
    this.zones.totalBloc.hidden = epuisee;
    return epuisee;
  }

  /* La longueur, la case ou la fixation ont change : on recalcule ce qui est
   * proposable. On garde la fixation choisie si elle reste compatible, sinon
   * on reprend la recommandee — le client a coche « Add a binding », il veut
   * une fixation, pas un ski nu. */
  surLongueur() {
    if (this.surRupture()) {
      this.zones.fixations.hidden = true;
      this.zones.peaux.hidden = true;
      return;
    }
    const e = this.entree;
    if (!e) {
      this.zones.fixations.hidden = true;
      this.zones.peaux.hidden = true;
      this.rafraichit();
      return;
    }
    if (!this.avecFixation) {
      this.fixation = null;
    } else if (!this.fixation || !e.fixations[this.fixation]) {
      this.fixation = this.recommandee(e);
    }
    if (!this.peauxPossibles()) this.avecPeaux = false;
    this.rendFixations(e);
    this.rendPeaux(e);
    this.rafraichit();
  }

  peauxPossibles() {
    return this.fixation === null || this.fixationsPeaux.includes(this.fixation);
  }

  rendFixations(e) {
    const ordre = this.donnees.ordre_fixations.filter((h) => e.fixations[h]);
    this.caseFixation.checked = this.avecFixation;
    this.zones.listeFixations.hidden = !this.avecFixation;
    if (this.zones.fixationAutre) this.zones.fixationAutre.hidden = !this.avecFixation;

    this.zones.listeFixations.innerHTML = ordre.map((handle) => {
      const f = e.fixations[handle];
      const nom = this.noms[handle] || {};
      return this.ligne({
        type: 'radio',
        nom: 'simply-fixation',
        valeur: handle,
        coche: this.fixation === handle,
        titre: nom.nom && nom.libelle ? `${nom.nom} — ${nom.libelle}` : f.nom,
        badge: f.recommandee ? this.textes.recommandee : '',
        prix: f.prix,
        popup: handle,
        dispo: f.vendable && f.dispo > 0,
      });
    }).join('');
    this.zones.fixations.hidden = false;
  }

  rendPeaux(e) {
    if (!e.peaux || !this.peauxPossibles()) {
      this.zones.peaux.hidden = true;
      return;
    }
    this.zones.peaux.querySelector('[data-liste="peaux"]').innerHTML = this.ligne({
      type: 'checkbox',
      nom: 'simply-peaux',
      valeur: '1',
      coche: this.avecPeaux,
      titre: this.textes.peaux_ajouter,
      prix: e.peaux.prix,
      dispo: e.peaux.dispo > 0,
    });
    this.zones.peaux.hidden = false;
  }

  /* Une ligne : marque, nom (+ badge), puis « CHF 199 · Details » dessous.
   * Le bouton Details est dans le label : simply-produit.js empeche le clic de
   * cocher la ligne. */
  ligne(o) {
    const e = (t) => this.echappe(t);
    const indispo = o.dispo === false;
    const sousLigne = [];
    if (o.prix) sousLigne.push(`<span class="simply-montage__prix">${e(this.prix(o.prix))}</span>`);
    if (indispo) sousLigne.push(`<span>${e(this.textes.epuise)}</span>`);
    if (o.popup) {
      sousLigne.push(`<button type="button" class="simply-montage__details" data-simply-popup="${e(o.popup)}">${e(this.textes.details)}</button>`);
    }
    return `
      <label class="simply-montage__ligne${indispo ? ' simply-montage__ligne--indispo' : ''}">
        <input type="${o.type}" name="${e(o.nom)}" value="${e(o.valeur)}"
               ${o.coche ? 'checked' : ''} ${indispo ? 'disabled' : ''}>
        <span class="simply-montage__marque" aria-hidden="true"></span>
        <span class="simply-montage__corps">
          <span class="simply-montage__nom">${e(o.titre)}${o.badge ? ` <span class="simply-montage__badge">${e(o.badge)}</span>` : ''}</span>
          ${sousLigne.length ? `<span class="simply-montage__detail">${sousLigne.join('<span aria-hidden="true"> · </span>')}</span>` : ''}
        </span>
      </label>`;
  }

  /* Ce qui part au panier, et ce que le client lit avant de cliquer. */
  composition() {
    const coche = this.querySelector('input[name="simply-longueur"]:checked');
    if (!coche) return null;
    const e = this.entree;
    const lignes = [];
    const ski = this.donnees ? this.donnees.titre : this.titre;

    if (e && this.fixation && e.fixations[this.fixation]) {
      const f = e.fixations[this.fixation];
      lignes.push({
        id: f.variante, quantity: 1,
        libelle: this.remplit(this.textes.ligne_montee, { ski, length: this.longueur, binding: f.nom }),
        prix: e.ski.prix + f.prix,
      });
    } else {
      lignes.push({
        id: Number(coche.dataset.variante), quantity: 1,
        libelle: this.remplit(this.textes.ligne_nue, { ski, length: this.longueur }),
        prix: Number(coche.dataset.prix),
      });
    }
    if (e && this.avecPeaux && e.peaux && this.peauxPossibles()) {
      lignes.push({
        id: e.peaux.variante, quantity: 1,
        libelle: this.remplit(this.textes.ligne_peaux, { ski, length: this.longueur }),
        prix: e.peaux.prix,
      });
    }
    return lignes;
  }

  rafraichit() {
    const lignes = this.composition();
    if (!lignes) return;
    const total = lignes.reduce((s, l) => s + l.prix, 0);
    this.zones.recap.innerHTML = lignes
      .map((l) => `<span class="simply-montage__poste">${this.echappe(l.libelle)}`
                + `<b>${this.echappe(this.prix(l.prix))}</b></span>`)
      .join('');
    this.zones.totalLibelle.textContent = this.fixation
      ? (this.textes.total_monte || this.textes.total)
      : this.textes.total;
    this.zones.total.textContent = this.prix(total);
    this.zones.message.textContent = '';
  }

  async ajoute() {
    const lignes = this.composition();
    if (!lignes) return;
    this.bouton.disabled = true;
    this.bouton.textContent = this.textes.ajout_en_cours || this.libelleBouton;
    try {
      const rep = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: lignes.map(({ id, quantity }) => ({ id, quantity })) }),
      });
      if (!rep.ok) {
        const err = await rep.json().catch(() => ({}));
        throw new Error(err.description || err.message || this.textes.ajout_echec);
      }
      // Le panier dans la langue en cours (/fr/cart, /de/cart) : window.routes vient de layout/theme.liquid.
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    } catch (err) {
      // On dit ce qui s'est passe et ce qu'on peut faire, pas « une erreur est survenue ».
      this.zones.message.textContent =
        this.remplit(this.textes.ajout_erreur, { error: err.message });
      this.bouton.disabled = false;
      this.bouton.textContent = this.libelleBouton;
    }
  }
}

if (!customElements.get('simply-montage')) {
  customElements.define('simply-montage', SimplyMontage);
}
